import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchAllNotifications,
  createNotification,
  archiveNotification,
  deleteNotification,
  clearError,
} from '../../store/slices/notificationsSlice';
import { format } from 'date-fns';

const NotificationManagementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { allNotifications, loading, error } = useAppSelector((state) => state.notifications);

  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [ttlHours, setTtlHours] = useState(24);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    dispatch(fetchAllNotifications({ limit: 100, offset: 0 }));
  }, [dispatch]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setMessage('');
    setTtlHours(24);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCreateNotification = async () => {
    if (!message.trim()) {
      setSnackbar({ open: true, message: 'Bitte geben Sie eine Nachricht ein', severity: 'error' });
      return;
    }

    try {
      await dispatch(createNotification({ message, ttl_hours: ttlHours })).unwrap();
      setSnackbar({ open: true, message: 'Benachrichtigung erfolgreich erstellt', severity: 'success' });
      handleCloseDialog();
      dispatch(fetchAllNotifications({ limit: 100, offset: 0 }));
    } catch (err: any) {
      setSnackbar({ open: true, message: err || 'Fehler beim Erstellen der Benachrichtigung', severity: 'error' });
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await dispatch(archiveNotification(id)).unwrap();
      setSnackbar({ open: true, message: 'Benachrichtigung archiviert', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err || 'Fehler beim Archivieren', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Möchten Sie diese Benachrichtigung wirklich löschen?')) {
      try {
        await dispatch(deleteNotification(id)).unwrap();
        setSnackbar({ open: true, message: 'Benachrichtigung gelöscht', severity: 'success' });
      } catch (err: any) {
        setSnackbar({ open: true, message: err || 'Fehler beim Löschen', severity: 'error' });
      }
    }
  };

  const handleRefresh = () => {
    dispatch(fetchAllNotifications({ limit: 100, offset: 0 }));
  };

  const getStatusChip = (notification: any) => {
    if (notification.archived) {
      return <Chip label="Archiviert" color="default" size="small" />;
    }
    if (notification.is_active) {
      return <Chip label="Aktiv" color="success" size="small" />;
    }
    return <Chip label="Abgelaufen" color="warning" size="small" />;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Benachrichtigungen verwalten
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mr: 1 }}
          >
            Aktualisieren
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Neue Benachrichtigung
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => dispatch(clearError())} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Nachricht</TableCell>
                <TableCell>TTL (Stunden)</TableCell>
                <TableCell>Erstellt am</TableCell>
                <TableCell>Läuft ab am</TableCell>
                <TableCell>Erstellt von</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allNotifications.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Keine Benachrichtigungen vorhanden
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {allNotifications.map((notification) => (
                <TableRow key={notification.id}>
                  <TableCell>{getStatusChip(notification)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 400, wordBreak: 'break-word' }}>
                      {notification.message}
                    </Typography>
                  </TableCell>
                  <TableCell>{notification.ttl_hours}</TableCell>
                  <TableCell>
                    {format(new Date(notification.created_at), 'dd.MM.yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    {format(new Date(notification.expires_at), 'dd.MM.yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    {notification.creator?.username || 'Unbekannt'}
                  </TableCell>
                  <TableCell align="right">
                    {!notification.archived && (
                      <IconButton
                        size="small"
                        onClick={() => handleArchive(notification.id)}
                        title="Archivieren"
                      >
                        <ArchiveIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(notification.id)}
                      title="Löschen"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Neue Benachrichtigung erstellen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nachricht"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="TTL (Stunden)"
            type="number"
            fullWidth
            value={ttlHours}
            onChange={(e) => setTtlHours(parseInt(e.target.value) || 24)}
            variant="outlined"
            inputProps={{ min: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Die Benachrichtigung wird automatisch nach {ttlHours} Stunden ausgeblendet.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button onClick={handleCreateNotification} variant="contained" disabled={loading}>
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationManagementPage;
